import React, { useEffect, useMemo, useState } from 'react';
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCol,
    CFormCheck,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormSwitch,
    CFormTextarea,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CNav,
    CNavItem,
    CNavLink,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilBullhorn, cilCloudUpload, cilPencil, cilPlus } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useStore } from '../../contexts/StoreContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Product } from '../../types/product';
import {
    DISCOUNT_FIXED,
    DISCOUNT_PERCENT,
    DiscountForm,
    DiscountItem,
    DiscountType,
    SERVICE_TYPE_COUPONS,
    SERVICE_TYPE_EVENTS,
    SERVICE_TYPE_OFFERS,
    SERVICE_TYPE_PROMOTIONS,
} from '../../types/marketing';
import { StatusBadge, discountLabel, formatCOP, formatDate, inputDateFromNow, toInputDate } from './marketingUtils';

const TABS: { type: DiscountType; label: string; newLabel: string; help: string; serviceType: number }[] = [
    {
        type: 'offers',
        label: 'Ofertas',
        newLabel: 'Nueva oferta',
        help: 'Descuento de precio (% o $) que se aplica solo: a toda la tienda por rango de precio (una a la vez) o a los productos que elijas.',
        serviceType: SERVICE_TYPE_OFFERS,
    },
    {
        type: 'promotions',
        label: 'Promociones',
        newLabel: 'Nueva promoción',
        help: 'Promociones de compra que se aplican en el carrito: "lleva 2, paga 1" (salen gratis las unidades más baratas) o "compra 2 ollas y te regalamos una cuchara" (el regalo se agrega solo al carrito en $0).',
        serviceType: SERVICE_TYPE_PROMOTIONS,
    },
    {
        type: 'coupons',
        label: 'Cupones',
        newLabel: 'Nuevo cupón',
        help: 'Código que el cliente escribe en el carrito para recibir el descuento en su pedido.',
        serviceType: SERVICE_TYPE_COUPONS,
    },
    {
        type: 'events',
        label: 'Eventos',
        newLabel: 'Nuevo evento',
        help: 'Noches temáticas, lanzamientos, horarios especiales... Se ven gratis en la sección Eventos de la app para los clientes de tu municipio.',
        serviceType: SERVICE_TYPE_EVENTS,
    },
];

const emptyForm = (): DiscountForm => ({
    name: '',
    description: '',
    discount_types_id: DISCOUNT_PERCENT,
    discount_value: '',
    start_date: inputDateFromNow(0),
    end_date: inputDateFromNow(7),
    min_value: '',
    max_value: '',
    is_active: true,
    product_ids: [],
    code: '',
    usage_limit: '',
    usage_limit_per_user: 1,
    location: '',
    promotion_type: 'nxm',
    buy_quantity: 2,
    pay_quantity: 1,
    gift_products_id: '',
    gift_variants_id: '',
    gift_quantity: 1,
});

const Discounts: React.FC = () => {
    const navigate = useNavigate();
    const { storeId } = useStore();
    const { showToast } = useNotification();

    const [activeTab, setActiveTab] = useState<DiscountType>('offers');
    const [items, setItems] = useState<DiscountItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);

    const [modalVisible, setModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<DiscountForm>(emptyForm());
    const [saving, setSaving] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    // Imagen del evento: archivo nuevo o la que ya tiene
    const [eventImage, setEventImage] = useState<File | null>(null);
    const [eventImagePreview, setEventImagePreview] = useState<string | null>(null);

    const isEvents = activeTab === 'events';
    const isPromotions = activeTab === 'promotions';
    // Oferta para toda la tienda (rango de precio) o para productos elegidos
    const [offerScope, setOfferScope] = useState<'store' | 'products'>('store');

    const setEventImageFile = (file: File | null) => {
        if (eventImage && eventImagePreview) URL.revokeObjectURL(eventImagePreview);
        setEventImage(file);
        setEventImagePreview(file ? URL.createObjectURL(file) : null);
    };

    const tab = TABS.find((t) => t.type === activeTab)!;

    const loadItems = () => {
        if (!storeId) return;
        setLoading(true);
        DomicilappService.getDiscounts(storeId, activeTab)
            .then((response) => setItems(response?.status === 200 ? response.data?.items ?? [] : []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setItems([]);
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, activeTab]);

    // Productos de la tienda para las promociones (sin combos)
    useEffect(() => {
        if (!storeId) return;
        setProducts([]);
        DomicilappService.getProductsByStore(storeId)
            .then((response) => setProducts((response?.data?.items ?? []).filter((p) => !p.is_combo)))
            .catch(() => setProducts([]));
    }, [storeId]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setProductSearch('');
        setEventImage(null);
        setEventImagePreview(null);
        setOfferScope('store');
        setModalVisible(true);
    };

    const openEdit = (item: DiscountItem) => {
        setEditingId(item.id);
        setForm({
            name: item.name,
            description: item.description ?? '',
            discount_types_id: item.discount_types_id,
            discount_value: item.discount_value,
            start_date: toInputDate(item.start_date),
            end_date: toInputDate(item.end_date),
            min_value: item.min_value ?? '',
            max_value: item.max_value ?? '',
            is_active: item.is_active,
            product_ids: (item.products ?? []).map((p) => p.id),
            code: item.code ?? '',
            usage_limit: item.usage_limit ?? '',
            usage_limit_per_user: item.usage_limit_per_user ?? 1,
            location: item.location ?? '',
            promotion_type: item.promotion_type ?? 'nxm',
            buy_quantity: item.buy_quantity ?? 2,
            pay_quantity: item.pay_quantity ?? 1,
            gift_products_id: item.gift_products_id ?? '',
            gift_variants_id: item.gift_variants_id ?? '',
            gift_quantity: item.gift_quantity ?? 1,
        });
        setProductSearch('');
        setEventImage(null);
        setEventImagePreview(item.image_url ?? null);
        setOfferScope((item.products ?? []).length > 0 ? 'products' : 'store');
        setModalVisible(true);
    };

    const handleSave = () => {
        setSaving(true);
        const body = {
            ...form,
            start_date: form.start_date.replace('T', ' ') + ':00',
            end_date: form.end_date.replace('T', ' ') + ':00',
            code: form.code.trim().toUpperCase(),
            // Oferta para toda la tienda: sin productos (aplica por rango de precio)
            product_ids: activeTab === 'offers' && offerScope === 'store' ? [] : form.product_ids,
            min_value: activeTab === 'offers' && offerScope === 'products' ? '' : form.min_value,
            max_value: activeTab === 'offers' && offerScope === 'products' ? '' : form.max_value,
        };
        let request;
        if (isEvents) {
            const fd = new FormData();
            fd.append('data', JSON.stringify({
                name: body.name,
                description: body.description,
                location: body.location,
                start_date: body.start_date,
                end_date: body.end_date,
                is_active: body.is_active,
                product_ids: body.product_ids,
            }));
            if (eventImage) fd.append('image', eventImage, eventImage.name);
            request = DomicilappService.saveEvent(storeId, fd, editingId ?? undefined);
        } else {
            request = DomicilappService.saveDiscount(storeId, activeTab, body, editingId ?? undefined);
        }
        request
            .then((response: any) => {
                if (response?.status === 200) {
                    showToast({ title: '¡Operación Exitosa!', message: 'Cambios guardados', type: 'success', delay: 3000 });
                    setModalVisible(false);
                    loadItems();
                } else {
                    showToast({ title: 'Revisa los datos', message: typeof response?.data?.error === 'string' ? response.data.error : response?.message, type: 'warning', delay: 4500 });
                }
            })
            .catch(() => showToast({ title: '¡Ha ocurrido un error!', message: 'No fue posible guardar', type: 'danger', delay: 3500 }))
            .finally(() => setSaving(false));
    };

    const handleToggle = (item: DiscountItem, isActive: boolean) => {
        DomicilappService.setDiscountStatus(storeId, activeTab, item.id, isActive).then((response: any) => {
            if (response?.status === 200) {
                loadItems();
            } else {
                showToast({ title: 'No se pudo cambiar', message: typeof response?.data?.error === 'string' ? response.data.error : response?.message, type: 'warning', delay: 4500 });
            }
        });
    };

    const filteredProducts = useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        return term ? products.filter((p) => p.name.toLowerCase().includes(term)) : products;
    }, [products, productSearch]);

    const toggleProduct = (productId: number) =>
        setForm((prev) => ({
            ...prev,
            product_ids: prev.product_ids.includes(productId) ? prev.product_ids.filter((id) => id !== productId) : [...prev.product_ids, productId],
        }));

    // Presentaciones del producto de regalo (un producto simple tiene una sola)
    const giftProduct = products.find((p) => p.id === Number(form.gift_products_id));
    const giftVariants = giftProduct?.variants ?? [];
    const variantName = (variant: { id: number; name: string; option_value_ids?: number[] }) => {
        if (!giftProduct?.has_variants) return giftProduct?.name ?? variant.name;
        const values = new Map<number, string>();
        (giftProduct.options ?? []).forEach((opt) => opt.values.forEach((val) => values.set(val.id, val.value)));
        return (variant.option_value_ids ?? []).map((id) => values.get(id)).filter(Boolean).join(' / ') || variant.name;
    };
    const selectGiftProduct = (productId: string) => {
        const product = products.find((p) => p.id === Number(productId));
        const variants = product?.variants ?? [];
        setForm({ ...form, gift_products_id: productId ? Number(productId) : '', gift_variants_id: variants.length === 1 ? variants[0].id : '' });
    };

    const conditionsText = (item: DiscountItem) => {
        if (activeTab === 'promotions') {
            const names = (item.products ?? []).map((p) => p.name);
            return `Aplica a: ${names.length > 3 ? `${names.slice(0, 3).join(', ')} y ${names.length - 3} más` : names.join(', ')}`;
        }
        if (activeTab === 'offers' && (item.products ?? []).length > 0) {
            const names = (item.products ?? []).map((p) => p.name);
            return names.length > 3 ? `${names.slice(0, 3).join(', ')} y ${names.length - 3} más` : names.join(', ');
        }
        if (activeTab === 'offers') {
            if (item.min_value && item.max_value) return `Productos de ${formatCOP(item.min_value)} a ${formatCOP(item.max_value)}`;
            if (item.min_value) return `Productos desde ${formatCOP(item.min_value)}`;
            if (item.max_value) return `Productos hasta ${formatCOP(item.max_value)}`;
            return 'Todos los productos';
        }
        if (activeTab === 'events') {
            const names = (item.products ?? []).map((p) => p.name);
            if (names.length === 0) return item.location || 'En la tienda';
            return names.length > 3 ? `${names.slice(0, 3).join(', ')} y ${names.length - 3} más` : names.join(', ');
        }
        const parts = [];
        if (item.min_value) parts.push(`Compra mínima ${formatCOP(item.min_value)}`);
        if (item.max_value) parts.push(`tope ${formatCOP(item.max_value)}`);
        parts.push(`${item.uses_count ?? 0}${item.usage_limit ? ` / ${item.usage_limit}` : ''} usos`);
        return parts.join(' · ');
    };

    return (
        <div className="discounts-page">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="fw-bold text-body mb-1">Descuentos y eventos</h3>
                    <p className="text-body-secondary small mb-0">Crearlos no tiene costo. Para darlos a conocer usa la Publicidad.</p>
                </div>
                <CButton color="dark" className="d-flex align-items-center gap-2" onClick={openCreate}>
                    <CIcon icon={cilPlus} size="sm" />
                    <span>{tab.newLabel}</span>
                </CButton>
            </div>

            <CCard className="border-0 shadow-sm rounded-3">
                <CCardBody className="p-4">
                    <CNav variant="underline" className="mb-3">
                        {TABS.map((t) => (
                            <CNavItem key={t.type}>
                                <CNavLink active={activeTab === t.type} onClick={() => setActiveTab(t.type)} style={{ cursor: 'pointer' }}>
                                    {t.label}
                                </CNavLink>
                            </CNavItem>
                        ))}
                    </CNav>
                    <p className="text-body-secondary small">{tab.help}</p>

                    <CTable responsive align="middle" hover className="border-top">
                        <CTableHead>
                            <CTableRow>
                                <CTableHeaderCell>Nombre</CTableHeaderCell>
                                <CTableHeaderCell>{isEvents ? 'Lugar' : isPromotions ? 'Promoción' : 'Descuento'}</CTableHeaderCell>
                                <CTableHeaderCell>{isEvents ? 'Cuándo' : 'Vigencia'}</CTableHeaderCell>
                                <CTableHeaderCell>Estado</CTableHeaderCell>
                                <CTableHeaderCell className="text-center">Activa</CTableHeaderCell>
                                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                            </CTableRow>
                        </CTableHead>
                        <CTableBody>
                            {loading ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={6} className="text-center py-5">
                                        <CSpinner size="sm" className="me-2" />
                                        <span className="text-body-secondary">Cargando...</span>
                                    </CTableDataCell>
                                </CTableRow>
                            ) : items.length === 0 ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={6} className="text-center py-5 text-body-secondary">
                                        Aún no tienes {tab.label.toLowerCase()}.
                                    </CTableDataCell>
                                </CTableRow>
                            ) : (
                                items.map((item) => (
                                    <CTableRow key={item.id}>
                                        <CTableDataCell>
                                            <div className="fw-semibold text-body d-flex align-items-center gap-2">
                                                {isEvents && item.image_url && (
                                                    <img src={item.image_url} alt="" className="rounded-2" style={{ width: '36px', height: '36px', objectFit: 'cover' }} />
                                                )}
                                                {item.name}
                                                {item.code && <CBadge color="dark" className="ms-2 fw-normal">{item.code}</CBadge>}
                                            </div>
                                            <small className="text-body-secondary d-block text-truncate" style={{ maxWidth: '360px' }}>{conditionsText(item)}</small>
                                        </CTableDataCell>
                                        <CTableDataCell className={isEvents ? 'small text-body' : 'fw-bold text-body'}>
                                            {isEvents
                                                ? item.location || 'En la tienda'
                                                : isPromotions
                                                    ? item.label
                                                    : discountLabel(item.discount_types_id, item.discount_value)}
                                        </CTableDataCell>
                                        <CTableDataCell className="small text-body-secondary">
                                            {formatDate(item.start_date)} → {formatDate(item.end_date)}
                                        </CTableDataCell>
                                        <CTableDataCell><StatusBadge status={item.status} /></CTableDataCell>
                                        <CTableDataCell className="text-center">
                                            <CFormSwitch className="d-inline-block" checked={item.is_active} onChange={(e) => handleToggle(item, e.target.checked)} />
                                        </CTableDataCell>
                                        <CTableDataCell className="text-end text-nowrap">
                                            {item.status === 'active' || item.status === 'scheduled' ? (
                                                <CButton
                                                    color="light"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => navigate(`/marketing/ads/create?type=${tab.serviceType}&content=${item.id}`)}
                                                >
                                                    <CIcon icon={cilBullhorn} size="sm" className="me-1" /> Publicitar
                                                </CButton>
                                            ) : null}
                                            <CButton color="light" size="sm" onClick={() => openEdit(item)}>
                                                <CIcon icon={cilPencil} size="sm" />
                                            </CButton>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))
                            )}
                        </CTableBody>
                    </CTable>
                </CCardBody>
            </CCard>

            {/* ---- FORMULARIO ---- */}
            <CModal visible={modalVisible} onClose={() => setModalVisible(false)} size="lg" alignment="center" backdrop="static">
                <CModalHeader>
                    <CModalTitle>{editingId ? `Editar ${tab.label.slice(0, -1).toLowerCase()}` : tab.newLabel}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <CRow className="g-3">
                        <CCol md={activeTab === 'coupons' ? 8 : 12}>
                            <CFormLabel className="small fw-semibold">Nombre</CFormLabel>
                            <CFormInput
                                value={form.name}
                                maxLength={isEvents ? 80 : 45}
                                placeholder={isEvents ? 'Ej. Noche de hamburguesas' : 'Ej. 20% en pizzas'}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </CCol>
                        {activeTab === 'coupons' && (
                            <CCol md={4}>
                                <CFormLabel className="small fw-semibold">Código</CFormLabel>
                                <CFormInput
                                    value={form.code}
                                    maxLength={30}
                                    placeholder="PIZZA20"
                                    className="text-uppercase"
                                    onChange={(e) => setForm({ ...form, code: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase() })}
                                />
                            </CCol>
                        )}
                        <CCol md={12}>
                            <CFormLabel className="small fw-semibold">{isEvents ? 'En qué consiste' : 'Descripción (opcional)'}</CFormLabel>
                            <CFormTextarea
                                rows={isEvents ? 4 : 2}
                                value={form.description}
                                placeholder={isEvents ? 'Ej. Música en vivo, 2x1 en la clásica y concurso de quién se come la más grande.' : ''}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </CCol>
                        {isEvents && (
                            <>
                                <CCol md={12}>
                                    <CFormLabel className="small fw-semibold">Lugar (opcional)</CFormLabel>
                                    <CFormInput value={form.location} maxLength={120} placeholder="Vacío = en la tienda" onChange={(e) => setForm({ ...form, location: e.target.value })} />
                                </CCol>
                                <CCol md={12}>
                                    <label
                                        htmlFor="event-image-input"
                                        className="d-flex align-items-center justify-content-center border rounded-3 bg-body-tertiary overflow-hidden"
                                        style={{ borderStyle: 'dashed', minHeight: '120px', cursor: 'pointer' }}
                                    >
                                        {eventImagePreview ? (
                                            <img src={eventImagePreview} alt="" style={{ maxHeight: '180px', maxWidth: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span className="text-body-secondary small d-flex align-items-center gap-2">
                                                <CIcon icon={cilCloudUpload} size="lg" /> Imagen del evento (horizontal)
                                            </span>
                                        )}
                                        <input id="event-image-input" type="file" accept="image/*" className="d-none" onChange={(e) => setEventImageFile(e.target.files?.[0] ?? null)} />
                                    </label>
                                </CCol>
                            </>
                        )}
                        {isPromotions && (
                            <>
                                <CCol md={12}>
                                    <CFormLabel className="small fw-semibold d-block">Tipo de promoción</CFormLabel>
                                    <CFormCheck
                                        inline
                                        type="radio"
                                        id="promotion-type-nxm"
                                        label="Lleva N, paga M (2x1, 3x2)"
                                        checked={form.promotion_type === 'nxm'}
                                        onChange={() => setForm({ ...form, promotion_type: 'nxm' })}
                                    />
                                    <CFormCheck
                                        inline
                                        type="radio"
                                        id="promotion-type-gift"
                                        label="Compra y te regalamos"
                                        checked={form.promotion_type === 'gift'}
                                        onChange={() => setForm({ ...form, promotion_type: 'gift' })}
                                    />
                                </CCol>
                                <CCol md={form.promotion_type === 'nxm' ? 6 : 4}>
                                    <CFormLabel className="small fw-semibold">{form.promotion_type === 'nxm' ? 'Lleva' : 'Por cada'}</CFormLabel>
                                    <CFormInput
                                        type="number"
                                        min={1}
                                        value={form.buy_quantity}
                                        onChange={(e) => setForm({ ...form, buy_quantity: e.target.value === '' ? '' : Number(e.target.value) })}
                                    />
                                    <small className="text-body-secondary">unidades de los productos elegidos abajo</small>
                                </CCol>
                                {form.promotion_type === 'nxm' ? (
                                    <CCol md={6}>
                                        <CFormLabel className="small fw-semibold">Paga</CFormLabel>
                                        <CFormInput
                                            type="number"
                                            min={1}
                                            value={form.pay_quantity}
                                            onChange={(e) => setForm({ ...form, pay_quantity: e.target.value === '' ? '' : Number(e.target.value) })}
                                        />
                                        <small className="text-body-secondary">
                                            {form.buy_quantity && form.pay_quantity ? `Se mostrará como ${form.buy_quantity}x${form.pay_quantity}` : ''}
                                        </small>
                                    </CCol>
                                ) : (
                                    <>
                                        <CCol md={5}>
                                            <CFormLabel className="small fw-semibold">Regalo</CFormLabel>
                                            <CFormSelect value={form.gift_products_id} onChange={(e) => selectGiftProduct(e.target.value)}>
                                                <option value="">Selecciona un producto</option>
                                                {products.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </CFormSelect>
                                            {giftVariants.length > 1 && (
                                                <CFormSelect
                                                    className="mt-2"
                                                    value={form.gift_variants_id}
                                                    onChange={(e) => setForm({ ...form, gift_variants_id: e.target.value ? Number(e.target.value) : '' })}
                                                >
                                                    <option value="">Presentación</option>
                                                    {giftVariants.map((v) => (
                                                        <option key={v.id} value={v.id}>{variantName(v)}</option>
                                                    ))}
                                                </CFormSelect>
                                            )}
                                        </CCol>
                                        <CCol md={3}>
                                            <CFormLabel className="small fw-semibold">Unidades de regalo</CFormLabel>
                                            <CFormInput
                                                type="number"
                                                min={1}
                                                value={form.gift_quantity}
                                                onChange={(e) => setForm({ ...form, gift_quantity: e.target.value === '' ? '' : Number(e.target.value) })}
                                            />
                                        </CCol>
                                    </>
                                )}
                            </>
                        )}
                        {!isEvents && !isPromotions && (
                        <>
                        <CCol md={6}>
                            <CFormLabel className="small fw-semibold">Tipo de descuento</CFormLabel>
                            <CFormSelect value={form.discount_types_id} onChange={(e) => setForm({ ...form, discount_types_id: Number(e.target.value) })}>
                                <option value={DISCOUNT_PERCENT}>Porcentaje (%)</option>
                                <option value={DISCOUNT_FIXED}>Valor fijo ($)</option>
                            </CFormSelect>
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel className="small fw-semibold">{form.discount_types_id === DISCOUNT_PERCENT ? 'Porcentaje' : 'Valor a descontar'}</CFormLabel>
                            <CFormInput
                                type="number"
                                min={0}
                                value={form.discount_value}
                                onChange={(e) => setForm({ ...form, discount_value: e.target.value === '' ? '' : Number(e.target.value) })}
                            />
                        </CCol>
                        </>
                        )}
                        <CCol md={6}>
                            <CFormLabel className="small fw-semibold">{isEvents ? 'Empieza' : 'Inicia'}</CFormLabel>
                            <CFormInput type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel className="small fw-semibold">Termina</CFormLabel>
                            <CFormInput type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                        </CCol>

                        {activeTab === 'offers' && (
                            <CCol md={12}>
                                <CFormLabel className="small fw-semibold d-block">Aplica a</CFormLabel>
                                <CFormCheck
                                    inline
                                    type="radio"
                                    id="offer-scope-store"
                                    label="Toda la tienda (por rango de precio)"
                                    checked={offerScope === 'store'}
                                    onChange={() => setOfferScope('store')}
                                />
                                <CFormCheck
                                    inline
                                    type="radio"
                                    id="offer-scope-products"
                                    label="Productos elegidos"
                                    checked={offerScope === 'products'}
                                    onChange={() => setOfferScope('products')}
                                />
                            </CCol>
                        )}
                        {activeTab === 'offers' && offerScope === 'store' && (
                            <>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Aplica a productos desde (opcional)</CFormLabel>
                                    <CFormInput type="number" min={0} placeholder="$0" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value === '' ? '' : Number(e.target.value) })} />
                                </CCol>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Hasta (opcional)</CFormLabel>
                                    <CFormInput type="number" min={0} placeholder="Sin límite" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value === '' ? '' : Number(e.target.value) })} />
                                </CCol>
                            </>
                        )}

                        {activeTab === 'coupons' && (
                            <>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Compra mínima (opcional)</CFormLabel>
                                    <CFormInput type="number" min={0} placeholder="$0" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value === '' ? '' : Number(e.target.value) })} />
                                </CCol>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Descuento máximo (opcional)</CFormLabel>
                                    <CFormInput type="number" min={0} placeholder="Sin tope" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value === '' ? '' : Number(e.target.value) })} />
                                </CCol>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Usos en total</CFormLabel>
                                    <CFormInput type="number" min={0} placeholder="Sin límite" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value === '' ? '' : Number(e.target.value) })} />
                                </CCol>
                                <CCol md={6}>
                                    <CFormLabel className="small fw-semibold">Usos por cliente</CFormLabel>
                                    <CFormInput type="number" min={1} value={form.usage_limit_per_user} onChange={(e) => setForm({ ...form, usage_limit_per_user: e.target.value === '' ? '' : Number(e.target.value) })} />
                                </CCol>
                            </>
                        )}

                        {((activeTab === 'offers' && offerScope === 'products') || isEvents || isPromotions) && (
                            <CCol md={12}>
                                <CFormLabel className="small fw-semibold">
                                    {isEvents ? 'Productos destacados (opcional)' : isPromotions ? 'Productos que activan la promoción' : 'Productos'} ({form.product_ids.length} elegidos)
                                </CFormLabel>
                                <CFormInput size="sm" className="mb-2" placeholder="Buscar producto..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} />
                                <div className="border rounded-3 p-2 bg-body-tertiary" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                    {filteredProducts.length === 0 && <small className="text-body-secondary">No hay productos.</small>}
                                    {filteredProducts.map((p) => (
                                        <CFormCheck
                                            key={p.id}
                                            id={`promo-product-${p.id}`}
                                            label={p.name}
                                            checked={form.product_ids.includes(p.id)}
                                            onChange={() => toggleProduct(p.id)}
                                        />
                                    ))}
                                </div>
                            </CCol>
                        )}
                    </CRow>
                    {activeTab === 'offers' && (
                        <CAlert color="secondary" className="small mt-3 mb-0 bg-body-tertiary text-body-secondary border">
                            El descuento se ve en la app en el precio de cada producto y se cobra así en el pedido. Si a un producto le
                            aplican varias ofertas, el cliente recibe el mejor precio.
                        </CAlert>
                    )}
                </CModalBody>
                <CModalFooter>
                    <CButton color="light" onClick={() => setModalVisible(false)}>Cancelar</CButton>
                    <CButton color="dark" disabled={saving} onClick={handleSave}>
                        {saving && <CSpinner size="sm" className="me-1" />} Guardar
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default Discounts;
